import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    FaArrowLeft,
    FaArrowRight,
    FaBuilding,
    FaCalendarDays,
    FaCircleExclamation,
    FaClock,
    FaFacebook,
    FaFacebookF,
    FaImages,
    FaInstagram,
    FaLink,
    FaLinkedinIn,
    FaNewspaper,
    FaTiktok,
    FaTwitter,
    FaUser,
    FaVideo,
    FaVimeoV,
    FaWhatsapp,
    FaYoutube,
} from 'react-icons/fa6';
import { Link, useParams } from 'react-router-dom';
import { fetchBlogBySlug } from '../services/blogService';
import '../styles/blog.css';

const PROVIDER_ICONS = {
    youtube: FaYoutube,
    instagram: FaInstagram,
    tiktok: FaTiktok,
    facebook: FaFacebook,
    x: FaTwitter,
    vimeo: FaVimeoV,
    other: FaLink,
};

const BlogDetail = () => {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetchBlogBySlug(slug);
                if (res.success) {
                    setBlog(res.data);
                } else {
                    setError('Artikel tidak ditemukan.');
                }
            } catch {
                setError('Gagal memuat artikel. Silakan coba lagi.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [slug]);

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    if (loading) {
        return (
            <div className="blog-detail-page">
                <div className="blog-detail-container">
                    <div className="skeleton skeleton-featured" />
                    <div className="skeleton skeleton-category" />
                    <div className="skeleton skeleton-title" />
                    <div className="skeleton skeleton-title-short" />
                    <div className="skeleton-meta">
                        <div className="skeleton skeleton-meta-item" style={{ width: 120 }} />
                        <div className="skeleton skeleton-meta-item" style={{ width: 140 }} />
                        <div className="skeleton skeleton-meta-item" style={{ width: 100 }} />
                    </div>
                    <div className="skeleton-tags">
                        <div className="skeleton skeleton-tag" />
                        <div className="skeleton skeleton-tag" />
                        <div className="skeleton skeleton-tag" />
                        <div className="skeleton skeleton-tag" />
                    </div>
                    <div className="skeleton skeleton-excerpt" />
                    <div className="skeleton-content-block">
                        <div className="skeleton skeleton-line" />
                        <div className="skeleton skeleton-line" />
                        <div className="skeleton skeleton-line" />
                        <div className="skeleton skeleton-line" />
                        <div className="skeleton skeleton-line" />
                        <div className="skeleton skeleton-line" />
                        <div className="skeleton skeleton-line" />
                    </div>
                    <div className="skeleton skeleton-section-title" />
                    <div className="skeleton-gallery">
                        <div className="skeleton skeleton-gallery-item" />
                        <div className="skeleton skeleton-gallery-item" />
                        <div className="skeleton skeleton-gallery-item" />
                        <div className="skeleton skeleton-gallery-item" />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="blog-detail-page">
                <div className="blog-detail-container">
                    <div className="blog-empty">
                        <FaCircleExclamation size={48} color="#e5e7eb" style={{ marginBottom: 16 }} />
                        <h3>Artikel Tidak Ditemukan</h3>
                        <p>{error || 'Artikel tidak tersedia.'}</p>
                        <Link to="/blog" className="blog-detail-back" style={{ marginTop: 16 }}>
                            <FaArrowLeft size={14} /> Kembali ke Blog
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const shareUrl = window.location.href;
    const shareText = encodeURIComponent(blog.title);

    const renderProviderIcon = (provider) => {
        const Icon = PROVIDER_ICONS[provider] || FaLink;
        return <Icon size={18} />;
    };

    const renderRelatedSidebar = () => {
        return (
            <aside className="blog-detail-sidebar">
                <div className="sidebar-ad-card">
                    <div className="sidebar-ad-icon">
                        <FaBuilding size={32} />
                    </div>
                    <h3 className="sidebar-ad-title">Cari Properti Impian?</h3>
                    <p className="sidebar-ad-desc">
                        Temukan properti terbaik dengan harga spesial hanya di Urbania. Konsultasi gratis dengan agen
                        profesional kami.
                    </p>
                    <a href="/properties" className="sidebar-ad-cta">
                        Jelajahi Properti <FaArrowRight size={13} />
                    </a>
                </div>

                {blog.related && blog.related.length > 0 && (
                    <div className="sidebar-section">
                        <h3>Artikel Lainnya</h3>
                        <div className="sidebar-related-list">
                            {blog.related.map((item) => (
                                <Link key={item.id} to={`/blog/${item.slug}`} className="sidebar-related-card">
                                    <div className="sidebar-related-img">
                                        {item.featured_image ? (
                                            <img src={item.featured_image} alt={item.title} loading="lazy" />
                                        ) : (
                                            <div className="placeholder">
                                                <FaNewspaper size={20} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="sidebar-related-body">
                                        <h4>{item.title}</h4>
                                        <span className="sidebar-related-meta">{item.reading_time} menit baca</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </aside>
        );
    };

    return (
        <div className="blog-detail-page">
            <Helmet>
                <title>{blog.meta_title} - Urbania</title>
                <meta name="description" content={blog.meta_description || ''} />
                <meta property="og:title" content={blog.meta_title} />
                <meta property="og:description" content={blog.meta_description || ''} />
                {blog.featured_image && <meta property="og:image" content={blog.featured_image} />}
            </Helmet>

            <div className="blog-detail-container">
                <Link to="/blog" className="blog-detail-back">
                    <FaArrowLeft size={14} /> Kembali ke Blog
                </Link>

                <div className="blog-detail-layout">
                    <div className="blog-detail-main">
                        <div className="blog-detail-featured">
                            {blog.featured_image ? (
                                <img src={blog.featured_image} alt={blog.title} />
                            ) : (
                                <div className="placeholder">
                                    <FaNewspaper size={56} />
                                </div>
                            )}
                        </div>

                        {blog.category && <span className="blog-detail-category">{blog.category.name}</span>}

                        <h1 className="blog-detail-title">{blog.title}</h1>

                        <div className="blog-detail-meta">
                            {blog.author && (
                                <span>
                                    <FaUser size={13} /> {blog.author.name}
                                </span>
                            )}
                            <span>
                                <FaCalendarDays size={13} /> {formatDate(blog.published_at)}
                            </span>
                            <span>
                                <FaClock size={13} /> {blog.reading_time} menit baca
                            </span>
                        </div>

                        {blog.tags && blog.tags.length > 0 && (
                            <div className="blog-detail-tags">
                                {blog.tags.map((tag) => (
                                    <span className="tag" key={tag.id}>
                                        {tag.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {blog.excerpt && (
                            <div className="blog-detail-excerpt">
                                <p>{blog.excerpt}</p>
                            </div>
                        )}

                        <div className="blog-detail-content" dangerouslySetInnerHTML={{ __html: blog.content }} />

                        {blog.gallery && blog.gallery.length > 0 && (
                            <div className="blog-detail-section">
                                <h3>
                                    <FaImages size={20} style={{ marginRight: 8, color: '#10b981' }} />
                                    Galeri
                                </h3>
                                <div className="blog-gallery-grid">
                                    {blog.gallery.map((img) => (
                                        <a key={img.id} href={img.url} target="_blank" rel="noopener noreferrer">
                                            <img src={img.url} alt={img.alt || 'Gallery'} loading="lazy" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {blog.embeds && blog.embeds.length > 0 && (
                            <div className="blog-detail-section">
                                <h3>
                                    <FaVideo size={20} style={{ marginRight: 8, color: '#dc3545' }} />
                                    Media Tertanam
                                </h3>
                                <div className="blog-embeds-grid">
                                    {blog.embeds.map((embed) => (
                                        <a
                                            key={embed.id}
                                            href={embed.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="blog-embed-card"
                                        >
                                            <div className="provider-icon">{renderProviderIcon(embed.provider)}</div>
                                            <div className="embed-info">
                                                <div className="provider-name">
                                                    {embed.provider.charAt(0).toUpperCase() + embed.provider.slice(1)}
                                                </div>
                                                <span className="embed-url">{embed.title || embed.url}</span>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {blog.related_properties && blog.related_properties.length > 0 && (
                            <div className="blog-detail-section">
                                <h3>
                                    <FaBuilding size={20} style={{ marginRight: 8, color: '#0d6efd' }} />
                                    Properti Terkait
                                </h3>
                                <div className="blog-related-props">
                                    {blog.related_properties.map((prop) => (
                                        <Link key={prop.id} to={`/properties/${prop.slug}`} className="prop-badge">
                                            <FaBuilding size={12} style={{ marginRight: 4 }} />
                                            {prop.title}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="blog-detail-share">
                            <span>Bagikan:</span>
                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Bagikan ke Facebook"
                            >
                                <FaFacebookF size={16} />
                            </a>
                            <a
                                href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Bagikan ke X"
                            >
                                <FaTwitter size={16} />
                            </a>
                            <a
                                href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Bagikan ke WhatsApp"
                            >
                                <FaWhatsapp size={16} />
                            </a>
                            <a
                                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Bagikan ke LinkedIn"
                            >
                                <FaLinkedinIn size={16} />
                            </a>
                        </div>
                    </div>

                    {renderRelatedSidebar()}
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;
